/**
 * Task Search Provider
 * Searches tasks by patient name, claim number (siniestro), and doctor name
 */

import { FileText } from 'lucide-react';
import { tasksApi } from '@/lib/api/tasks.api';
import type { Task, TaskStatus } from '@/types/task.types';
import type { SearchProvider, SearchResult, SearchAction } from '../types';

const MAX_RESULTS = 6;

// Status display config with labels and colors
const STATUS_CONFIG: Record<TaskStatus, { label: string; bg: string; text: string }> = {
  verificacion: { label: 'Verificación', bg: 'bg-yellow-100', text: 'text-yellow-700' },
  en_proceso: { label: 'En Proceso', bg: 'bg-blue-100', text: 'text-blue-700' },
  detenidos: { label: 'Detenido', bg: 'bg-orange-100', text: 'text-orange-700' },
  pagado: { label: 'Pagado', bg: 'bg-green-100', text: 'text-green-700' },
  negados: { label: 'Negado', bg: 'bg-red-100', text: 'text-red-700' },
  cancelado: { label: 'Cancelado', bg: 'bg-gray-100', text: 'text-gray-700' },
};

// Format currency
function formatMoney(amount: number | string | null | undefined): string {
  if (amount === null || amount === undefined) return '';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function createTaskResult(task: Task): SearchResult<Task> {
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.verificacion;

  // Build keywords for fuzzy matching
  const keywords: string[] = [];
  if (task.patientName) keywords.push(task.patientName);
  if (task.claimNumber) keywords.push(task.claimNumber);
  if (task.doctorName) keywords.push(task.doctorName);
  if (task.insurerName) keywords.push(task.insurerName);
  if (task.description) keywords.push(task.description);
  if (task.uploadFolio) keywords.push(task.uploadFolio);
  if (task.invoiceFolio) keywords.push(task.invoiceFolio);

  // Simple icon without badge (status will be on the right)
  const icon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800">
      <FileText className="h-5 w-5 text-slate-600 dark:text-slate-400" />
    </div>
  );

  // Get folio to display
  const folio = task.uploadFolio || task.invoiceFolio;
  const folioLabel = task.uploadFolio ? 'Folio' : 'Factura';

  // Full-width subtitle with left info and right badges
  const subtitle = (
    <div className="flex w-full items-center justify-between gap-4">
      {/* Left: Doctor and Insurer */}
      <span className="text-xs text-muted-foreground">
        {[task.doctorName, task.insurerName].filter(Boolean).join(' • ')}
      </span>

      {/* Right: Folio, Amount, Status */}
      <div className="flex shrink-0 items-center gap-3">
        {folio && (
          <span className="text-xs text-muted-foreground">
            {folioLabel}: <span className="font-medium text-foreground/80">{folio}</span>
          </span>
        )}
        {task.amount && (
          <span className="text-sm font-semibold text-foreground">{formatMoney(task.amount)}</span>
        )}
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusConfig.bg} ${statusConfig.text}`}
        >
          {statusConfig.label}
        </span>
      </div>
    </div>
  );

  return {
    id: task.id,
    title: task.patientName || task.description || `Trámite #${task.id.slice(0, 8)}`,
    subtitle,
    icon,
    keywords,
    data: task,
    providerId: 'tasks',
  };
}

export const TaskSearchProvider: SearchProvider<Task> = {
  id: 'tasks',
  name: 'Trámites',
  icon: <FileText className="h-4 w-4" />,
  priority: 2,
  placeholder: 'Buscar por paciente, siniestro, médico, aseguradora, folio...',

  async search(query: string): Promise<SearchResult<Task>[]> {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const response = await tasksApi.list({
        search: query,
        limit: MAX_RESULTS,
      });

      return response.data.map(createTaskResult);
    } catch (error) {
      console.error('[TaskSearchProvider] Search error:', error);
      return [];
    }
  },

  getActions(task: Task): SearchAction[] {
    const actions: SearchAction[] = [
      {
        id: 'view',
        label: 'Ver trámite',
        shortcut: '⌘↵',
        path: `/tasks/${task.id}`,
      },
    ];

    // Add doctor navigation if we have doctor info
    if (task.doctorId) {
      actions.push({
        id: 'doctor',
        label: `Ver médico${task.doctorName ? `: ${task.doctorName}` : ''}`,
        path: `/doctors/${task.doctorId}`,
      });
    }

    return actions;
  },
};
